import { motion } from 'framer-motion';
import { Settings as SettingsIcon, Sun, Moon, User, Mail, Shield, Globe } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

export const Settings = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, language, changeLanguage } = useLanguage();

  const getRoleLabel = (role) => {
    switch (role) {
      case 'patient': return t('patient');
      case 'medical_professional': return t('medical');
      case 'admin': return t('admin');
      default: return role;
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-8" data-testid="settings-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center gap-3 mb-8">
          <SettingsIcon className="w-6 h-6 text-primary" />
          <h1 className="text-3xl font-bold">{t('settingsTitle')}</h1>
        </div>

        {/* Profile section */}
        <div className="mb-8">
          <h2 className="label-medical mb-4">{t('profile')}</h2>
          <Card className="p-6 rounded-2xl space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h3 className="font-bold text-lg">{user?.name}</h3>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">{user?.email}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">{getRoleLabel(user?.role)}</span>
            </div>
          </Card>
        </div>

        {/* Language */}
        <div className="mb-8">
          <h2 className="label-medical mb-4">{t('language')}</h2>
          <Card className="p-6 rounded-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <Label className="text-base font-semibold">{t('selectLanguage')}</Label>
                  <p className="text-sm text-muted-foreground">
                    {language === 'en' ? t('english') : t('portuguese')}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={language === 'en' ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-full"
                  onClick={() => changeLanguage('en')}
                  data-testid="lang-en-settings"
                >
                  🇬🇧 EN
                </Button>
                <Button
                  variant={language === 'pt' ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-full"
                  onClick={() => changeLanguage('pt')}
                  data-testid="lang-pt-settings"
                >
                  🇵🇹 PT
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Appearance */}
        <div className="mb-8">
          <h2 className="label-medical mb-4">{t('appearance')}</h2>
          <Card className="p-6 rounded-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {theme === 'light' ? (
                  <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                    <Sun className="w-6 h-6 text-yellow-600" />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-indigo-900/30 flex items-center justify-center">
                    <Moon className="w-6 h-6 text-indigo-400" />
                  </div>
                )}
                <div>
                  <Label className="text-base font-semibold">{t('darkMode')}</Label>
                  <p className="text-sm text-muted-foreground">
                    {theme === 'light' ? t('lightThemeActive') : t('darkThemeActive')}
                  </p>
                </div>
              </div>
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={toggleTheme}
                data-testid="theme-switch"
              />
            </div>
          </Card>
        </div>

        {/* About */}
        <div>
          <h2 className="label-medical mb-4">{t('about')}</h2>
          <Card className="p-6 rounded-2xl">
            <h3 className="font-bold mb-2">{t('appName')}</h3>
            <p className="text-muted-foreground text-sm mb-4">
              {t('aboutDescription')}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('version')}
            </p>
          </Card>
        </div>
      </motion.div>
    </div>
  );
};
